import {
  INSPECTION_SCHEDULE_CADENCES,
  type CreateInspectionScheduleInput,
  type InspectionScheduleCadence,
  type InspectionScheduleScope,
} from '@sitewise/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { fetchMe } from '../../../api/auth.js';
import { listBuildings, listUnits } from '../../../api/buildings.js';
import {
  createInspectionSchedule,
  deleteInspectionSchedule,
  listInspectionSchedules,
  pauseInspectionSchedule,
  resumeInspectionSchedule,
  runInspectionScheduleNow,
  type InspectionScheduleSummary,
} from '../../../api/inspection-schedules.js';
import { listInspectionTemplates } from '../../../api/inspection-templates.js';
import { ApiError } from '../../../lib/api-client.js';

import { cadenceLabel, formatDate } from './lib.js';

export function InspectionSchedulesPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isAdmin = meQuery.data?.user.role === 'admin';

  const schedQuery = useQuery({
    queryKey: ['inspection-schedules', 'list'],
    queryFn: () => listInspectionSchedules(),
  });

  const tplQuery = useQuery({
    queryKey: ['inspection-templates', 'all'],
    queryFn: () => listInspectionTemplates({}),
  });

  const tplById = useMemo(
    () => new Map(tplQuery.data?.templates.map((t) => [t.id, t]) ?? []),
    [tplQuery.data],
  );

  const pauseMut = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      paused ? pauseInspectionSchedule(id) : resumeInspectionSchedule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection-schedules'] }),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not update'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteInspectionSchedule(id),
    onSuccess: () => {
      toast.success('Schedule deleted.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-schedules'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
  });

  const runMut = useMutation({
    mutationFn: (id: string) => runInspectionScheduleNow(id),
    onSuccess: (r) => {
      toast.success(`Generated ${r.inspectionsCreated} inspection${r.inspectionsCreated === 1 ? '' : 's'}.`);
      void queryClient.invalidateQueries({ queryKey: ['inspections'] });
      void queryClient.invalidateQueries({ queryKey: ['inspection-schedules'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Run failed'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-fg-muted">
          Schedules generate inspections on a recurring cadence. The worker checks every 15 minutes.
        </p>
        {isAdmin ? (
          <div className="ml-auto">
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" />
              New schedule
            </Button>
          </div>
        ) : null}
      </div>

      {schedQuery.isError ? (
        <ErrorState description="Could not load schedules." />
      ) : schedQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (schedQuery.data?.schedules.length ?? 0) === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No schedules yet"
          description="Add a schedule to make inspections recur automatically."
          action={
            isAdmin ? <Button onClick={() => setShowCreate(true)}>New schedule</Button> : null
          }
        />
      ) : (
        <DataTable<InspectionScheduleSummary>
          rowKey={(r) => r.id}
          rows={schedQuery.data!.schedules}
          columns={[
            {
              id: 'template',
              header: 'Template',
              description: 'The inspection form this schedule runs, and what it covers.',
              cell: (r) => {
                const t = tplById.get(r.templateId);
                return (
                  <div className="min-w-0">
                    <div className="truncate font-medium">{t?.name ?? r.templateId.slice(0, 8)}</div>
                    <div className="text-xs text-fg-muted">
                      {(r.scope as { kind: string }).kind.replace(/_/g, ' ')}
                    </div>
                  </div>
                );
              },
            },
            {
              id: 'cadence',
              header: 'Cadence',
              description: 'How often the inspection repeats.',
              cell: (r) => (
                <span className="text-sm">{cadenceLabel(r.cadence, r.customIntervalDays)}</span>
              ),
            },
            {
              id: 'next',
              header: 'Next run',
              description: 'When the next inspection will be created.',
              cell: (r) => (
                <span className="text-sm tabular-nums">{formatDate(r.nextRunAt)}</span>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              description: 'Whether the schedule is running or paused.',
              cell: (r) =>
                r.isPaused ? (
                  <span className="text-sm text-fg-muted">Paused</span>
                ) : (
                  <span className="text-sm text-success">Active</span>
                ),
            },
            {
              id: 'actions',
              header: '',
              cell: (r) => (
                <div className="flex justify-end gap-1">
                  {isAdmin ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runMut.mutate(r.id)}
                        disabled={runMut.isPending}
                      >
                        Run now
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => pauseMut.mutate({ id: r.id, paused: !r.isPaused })}
                        aria-label={r.isPaused ? 'Resume' : 'Pause'}
                      >
                        {r.isPaused ? (
                          <Play className="h-4 w-4" />
                        ) : (
                          <Pause className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          if (confirm('Delete this schedule?')) deleteMut.mutate(r.id);
                        }}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : null}
                </div>
              ),
              className: 'text-right',
            },
          ]}
        />
      )}

      {showCreate ? <CreateScheduleSheet onClose={() => setShowCreate(false)} /> : null}
    </div>
  );
}

interface FormState {
  templateId: string;
  scopeKind: InspectionScheduleScope['kind'];
  buildingId: string;
  unitIds: string[];
  cadence: InspectionScheduleCadence;
  customIntervalDays: string;
  dayOfWeek: string;
  dayOfMonth: string;
  startsAt: string;
}

function CreateScheduleSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const tplId = useId();
  const scopeId = useId();
  const buildingId = useId();
  const cadenceId = useId();
  const customIntervalId = useId();
  const startsAtId = useId();

  const [form, setForm] = useState<FormState>({
    templateId: '',
    scopeKind: 'whole_building',
    buildingId: '',
    unitIds: [],
    cadence: 'monthly',
    customIntervalDays: '',
    dayOfWeek: '',
    dayOfMonth: '',
    startsAt: '',
  });
  const [error, setError] = useState<string | null>(null);

  const tplQuery = useQuery({
    queryKey: ['inspection-templates', 'published'],
    queryFn: () => listInspectionTemplates({ status: ['published'] }),
  });

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'all'],
    queryFn: () => listBuildings({ limit: 100 }),
  });

  const unitsQuery = useQuery({
    queryKey: ['building', form.buildingId, 'units'],
    queryFn: () => listUnits(form.buildingId),
    enabled: Boolean(form.buildingId) && form.scopeKind === 'specific_units',
  });

  const create = useMutation({
    mutationFn: () => {
      const scope: InspectionScheduleScope =
        form.scopeKind === 'specific_units'
          ? { kind: 'specific_units', buildingId: form.buildingId, unitIds: form.unitIds }
          : ({ kind: form.scopeKind, buildingId: form.buildingId } as InspectionScheduleScope);

      const input: CreateInspectionScheduleInput = {
        templateId: form.templateId,
        scope,
        cadence: form.cadence,
        customIntervalDays:
          form.cadence === 'custom' && form.customIntervalDays
            ? Number(form.customIntervalDays)
            : null,
        dayOfWeek: form.dayOfWeek === '' ? null : Number(form.dayOfWeek),
        dayOfMonth: form.dayOfMonth === '' ? null : Number(form.dayOfMonth),
        assignToUserId: null,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : undefined,
      };
      return createInspectionSchedule(input);
    },
    onSuccess: () => {
      toast.success('Schedule created.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-schedules'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create schedule');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>New inspection schedule</SheetTitle>
          <SheetDescription>
            Pick a template and a building, choose the recurrence, and we'll generate inspections
            automatically.
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-3 overflow-y-auto" onSubmit={handleSubmit}>
          <div className="grid gap-1.5">
            <Label htmlFor={tplId}>Template</Label>
            <select
              id={tplId}
              required
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.templateId}
              onChange={(e) => setForm({ ...form, templateId: e.target.value })}
            >
              <option value="">Select…</option>
              {tplQuery.data?.templates.map((t) => (
                <option key={t.id} value={t.id} disabled={t.publishedVersion === null}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={buildingId}>Building</Label>
            <select
              id={buildingId}
              required
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.buildingId}
              onChange={(e) => setForm({ ...form, buildingId: e.target.value, unitIds: [] })}
            >
              <option value="">Select…</option>
              {buildingsQuery.data?.buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={scopeId}>Scope</Label>
            <select
              id={scopeId}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.scopeKind}
              onChange={(e) =>
                setForm({
                  ...form,
                  scopeKind: e.target.value as InspectionScheduleScope['kind'],
                  unitIds: [],
                })
              }
            >
              <option value="whole_building">Whole building (one inspection)</option>
              <option value="all_units_in_building">All units in building</option>
              <option value="specific_units">Specific units</option>
              <option value="common_area">Common area</option>
            </select>
          </div>

          {form.scopeKind === 'specific_units' && unitsQuery.data ? (
            <div className="grid gap-1.5">
              <Label>Units</Label>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-border bg-surface p-2">
                {unitsQuery.data.units.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.unitIds.includes(u.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForm({ ...form, unitIds: [...form.unitIds, u.id] });
                        } else {
                          setForm({
                            ...form,
                            unitIds: form.unitIds.filter((id) => id !== u.id),
                          });
                        }
                      }}
                    />
                    {u.unitNumber}
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={cadenceId}>Cadence</Label>
            <select
              id={cadenceId}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.cadence}
              onChange={(e) =>
                setForm({ ...form, cadence: e.target.value as InspectionScheduleCadence })
              }
            >
              {INSPECTION_SCHEDULE_CADENCES.map((c) => (
                <option key={c} value={c}>
                  {cadenceLabel(c)}
                </option>
              ))}
            </select>
          </div>

          {form.cadence === 'custom' ? (
            <div className="grid gap-1.5">
              <Label htmlFor={customIntervalId}>Every (days)</Label>
              <Input
                id={customIntervalId}
                type="number"
                min={1}
                max={3650}
                required
                value={form.customIntervalDays}
                onChange={(e) => setForm({ ...form, customIntervalDays: e.target.value })}
              />
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={startsAtId}>Starts (optional)</Label>
            <Input
              id={startsAtId}
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                create.isPending ||
                !form.templateId ||
                !form.buildingId ||
                (form.cadence === 'custom' && !form.customIntervalDays) ||
                (form.scopeKind === 'specific_units' && form.unitIds.length === 0)
              }
            >
              {create.isPending ? 'Creating…' : 'Create schedule'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
